class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.rangeMin = this.querySelector('.range-slider__input--min');
    this.rangeMax = this.querySelector('.range-slider__input--max');
    this.trackFilled = this.querySelector('.range-slider__track-filled');

    // Prefer explicit decimal inputs, but fall back to text inputs if necessary
    this.textInputs = Array.from(this.querySelectorAll('input[inputmode="decimal"]'));
    if (this.textInputs.length < 2) {
      this.textInputs = Array.from(this.querySelectorAll('input')).filter((i) => i.type === 'text');
    }

    this.textMin = this.textInputs[0] || null;
    this.textMax = this.textInputs[1] || null;

  this.onRangeInput = this.onRangeInput.bind(this);
  this.onTextChange = this.onTextChange.bind(this);
  this.onKeyDown = this.onKeyDown.bind(this);
  this.onTrackPointerDown = this.onTrackPointerDown.bind(this);
  this.onDocumentPointerMove = this.onDocumentPointerMove.bind(this);
  this.onDocumentPointerUp = this.onDocumentPointerUp.bind(this);

    if (this.rangeMin) this.rangeMin.addEventListener('input', this.onRangeInput);
    if (this.rangeMax) this.rangeMax.addEventListener('input', this.onRangeInput);

    // Ensure pointer interactions focus the range and capture pointer for move events
    if (this.rangeMin) {
      this.rangeMin.addEventListener('pointerdown', (ev) => {
        try {
          // debug: log pointerdown on min thumb
          console.debug && console.debug('PriceRange:pointerdown', 'min', ev.pointerId);
          ev.currentTarget.focus();
          ev.currentTarget.setPointerCapture && ev.currentTarget.setPointerCapture(ev.pointerId);
          // bring this thumb above the other so it receives move events
          this.rangeMin.style.zIndex = 4;
          if (this.rangeMax) this.rangeMax.style.zIndex = 2;
        } catch (e) {}
      });
      this.rangeMin.addEventListener('pointerup', (ev) => {
        try {
          // debug: log pointerup on min thumb
          console.debug && console.debug('PriceRange:pointerup', 'min', ev.pointerId);
          ev.currentTarget.releasePointerCapture && ev.currentTarget.releasePointerCapture(ev.pointerId);
          this.rangeMin.style.zIndex = '';
          if (this.rangeMax) this.rangeMax.style.zIndex = '';
        } catch (e) {}
      });
      this.rangeMin.addEventListener('pointercancel', (ev) => {
        try { ev.currentTarget.releasePointerCapture && ev.currentTarget.releasePointerCapture(ev.pointerId); } catch (e) {}
      });
      // mousedown fallback for older browsers
      this.rangeMin.addEventListener('mousedown', (ev) => {
        try { this.rangeMin.style.zIndex = 3; if (this.rangeMax) this.rangeMax.style.zIndex = 2; } catch (e) {}
      });
      document.addEventListener('mouseup', () => { try { this.rangeMin.style.zIndex = ''; if (this.rangeMax) this.rangeMax.style.zIndex = ''; } catch (e) {} });
    }
    if (this.rangeMax) {
      this.rangeMax.addEventListener('pointerdown', (ev) => {
        try {
          // debug: log pointerdown on max thumb
          console.debug && console.debug('PriceRange:pointerdown', 'max', ev.pointerId);
          ev.currentTarget.focus();
          ev.currentTarget.setPointerCapture && ev.currentTarget.setPointerCapture(ev.pointerId);
          this.rangeMax.style.zIndex = 4;
          if (this.rangeMin) this.rangeMin.style.zIndex = 2;
        } catch (e) {}
      });
      this.rangeMax.addEventListener('pointerup', (ev) => {
        try {
          // debug: log pointerup on max thumb
          console.debug && console.debug('PriceRange:pointerup', 'max', ev.pointerId);
          ev.currentTarget.releasePointerCapture && ev.currentTarget.releasePointerCapture(ev.pointerId);
          this.rangeMax.style.zIndex = '';
          if (this.rangeMin) this.rangeMin.style.zIndex = '';
        } catch (e) {}
      });
      this.rangeMax.addEventListener('pointercancel', (ev) => {
        try { ev.currentTarget.releasePointerCapture && ev.currentTarget.releasePointerCapture(ev.pointerId); } catch (e) {}
      });
      this.rangeMax.addEventListener('mousedown', (ev) => {
        try { this.rangeMax.style.zIndex = 3; if (this.rangeMin) this.rangeMin.style.zIndex = 2; } catch (e) {}
      });
      document.addEventListener('mouseup', () => { try { this.rangeMax.style.zIndex = ''; if (this.rangeMin) this.rangeMin.style.zIndex = ''; } catch (e) {} });
    }

    if (this.textMin) {
      this.textMin.addEventListener('change', this.onTextChange);
      this.textMin.addEventListener('input', this.onTextChange);
      this.textMin.addEventListener('keydown', this.onKeyDown);
    }
    if (this.textMax) {
      this.textMax.addEventListener('change', this.onTextChange);
      this.textMax.addEventListener('input', this.onTextChange);
      this.textMax.addEventListener('keydown', this.onKeyDown);
    }

    // Pointer fallback: listen on the slider container to implement a robust drag
    this.rangeSlider = this.querySelector('.facets__range-slider');
    this.activeThumb = null;
    if (this.rangeSlider) {
      this.rangeSlider.addEventListener('pointerdown', this.onTrackPointerDown);
    }

    // initialize positions
    this.syncAll();

    // debug: confirm component upgraded and elements found
    if (window && window.console && window.console.debug) {
      console.debug('PriceRange:init', { rangeMin: this.rangeMin, rangeMax: this.rangeMax, textMin: this.textMin, textMax: this.textMax });
    }
  }

  onRangeInput(e) {
    if (!this.rangeMin || !this.rangeMax) return;
    let minVal = Number(this.rangeMin.value || 0);
    let maxVal = Number(this.rangeMax.value || 0);

    // Prevent crossing
    if (minVal > maxVal) {
      if (e.currentTarget === this.rangeMin) {
        minVal = maxVal;
        this.rangeMin.value = String(minVal);
      } else {
        maxVal = minVal;
        this.rangeMax.value = String(maxVal);
      }
    }

    // Sync to numeric inputs
    if (this.textMin) this.textMin.value = minVal === 0 ? '' : String(minVal);
    if (this.textMax) this.textMax.value = maxVal === 0 ? '' : String(maxVal);

    // Notify form listeners that inputs changed (programmatic updates need an event)
    try {
      if (this.textMin) this.textMin.dispatchEvent(new Event('input', { bubbles: true }));
      if (this.textMax) this.textMax.dispatchEvent(new Event('input', { bubbles: true }));
      // also dispatch on the enclosing form as a fallback
      const enclosingForm = this.closest && this.closest('form');
      if (enclosingForm) enclosingForm.dispatchEvent(new Event('input', { bubbles: true }));
    } catch (e) {}

    // Helpful debug when slider is used during development
    if (window && window.console && window.console.debug) {
      console.debug('PriceRange:onRangeInput', { minVal, maxVal });
    }

    this.updateTrack();
  }

  onTextChange(e) {
    const input = e.currentTarget;
    const raw = input.value.replace(/[^0-9.]/g, '');
    let value = Number(raw || 0);

    const minAttr = Number(input.getAttribute('data-min') || 0);
    const maxAttr = Number(input.getAttribute('data-max') || input.getAttribute('placeholder') || 0);

    if (isNaN(value)) value = 0;
    if (value < minAttr) value = minAttr;
    if (value > maxAttr) value = maxAttr;

    input.value = value === 0 ? '' : String(value);

    // Sync to ranges
    if (this.rangeMin && this.rangeMax && this.textMin && this.textMax) {
      const a = Number(this.textMin.value || 0);
      const b = Number(this.textMax.value || 0);
      if (a > b) {
        if (input === this.textMin) {
          this.textMin.value = String(b);
        } else {
          this.textMax.value = String(a);
        }
      }
      this.rangeMin.value = String(Number(this.textMin.value || 0));
      this.rangeMax.value = String(Number(this.textMax.value || 0));
      this.updateTrack();
    }
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  syncAll() {
    // Ensure ranges reflect text inputs (or defaults)
    const minAttr = this.rangeMin ? Number(this.rangeMin.getAttribute('min') || 0) : 0;
    const maxAttr = this.rangeMax ? Number(this.rangeMax.getAttribute('max') || 0) : 0;

    if (this.rangeMin && this.rangeMax) {
      if (this.textMin && this.textMin.value) this.rangeMin.value = String(Number(this.textMin.value));
      if (this.textMax && this.textMax.value) this.rangeMax.value = String(Number(this.textMax.value));

      // fallback defaults
      if (!this.rangeMin.value) this.rangeMin.value = String(minAttr);
      if (!this.rangeMax.value) this.rangeMax.value = String(maxAttr);
    }

    this.updateTrack();
  }

  onTrackPointerDown(e) {
    // When the native inputs are overlapped, native hit-testing can favour one input.
    // Use this pointerdown on the container to pick the nearest thumb and drive values via pointermove.
    if (!this.rangeMin || !this.rangeMax) return;
    e.preventDefault();
    const rect = this.rangeSlider.getBoundingClientRect();
    const clientX = e.clientX;
    const min = Number(this.rangeMin.getAttribute('min') || 0);
    const max = Number(this.rangeMax.getAttribute('max') || 0);
    const px = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const percent = px / rect.width;
    const valueAtPointer = Math.round(min + percent * (max - min));

    const a = Number(this.rangeMin.value || 0);
    const b = Number(this.rangeMax.value || 0);
    // choose the closer thumb, break ties in favour of min when pointer is left half
    const distToMin = Math.abs(valueAtPointer - a);
    const distToMax = Math.abs(valueAtPointer - b);
    if (distToMin <= distToMax) this.activeThumb = 'min';
    else this.activeThumb = 'max';

    // Immediately update the chosen thumb
    if (this.activeThumb === 'min') {
      this.rangeMin.value = String(Math.min(valueAtPointer, Number(this.rangeMax.value || max)));
    } else {
      this.rangeMax.value = String(Math.max(valueAtPointer, Number(this.rangeMin.value || min)));
    }
    if (this.textMin) this.textMin.value = this.rangeMin.value === '0' ? '' : String(Number(this.rangeMin.value || 0));
    if (this.textMax) this.textMax.value = this.rangeMax.value === '0' ? '' : String(Number(this.rangeMax.value || 0));
    try {
      if (this.textMin) this.textMin.dispatchEvent(new Event('input', { bubbles: true }));
      if (this.textMax) this.textMax.dispatchEvent(new Event('input', { bubbles: true }));
    } catch (e) {}
    this.updateTrack();

    // start listening to moves on document so dragging outside the element still works
    document.addEventListener('pointermove', this.onDocumentPointerMove);
    document.addEventListener('pointerup', this.onDocumentPointerUp, { once: true });
    // debug
    console.debug && console.debug('PriceRange:trackPointerDown', { active: this.activeThumb, valueAtPointer });
  }

  onDocumentPointerMove(e) {
    if (!this.activeThumb || !this.rangeSlider) return;
    const rect = this.rangeSlider.getBoundingClientRect();
    const clientX = e.clientX;
    const min = Number(this.rangeMin.getAttribute('min') || 0);
    const max = Number(this.rangeMax.getAttribute('max') || 0);
    const px = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const percent = px / rect.width;
    const valueAtPointer = Math.round(min + percent * (max - min));

    if (this.activeThumb === 'min') {
      const newVal = Math.min(valueAtPointer, Number(this.rangeMax.value || max));
      this.rangeMin.value = String(newVal);
      if (this.textMin) this.textMin.value = newVal === 0 ? '' : String(newVal);
      try {
        if (this.textMin) this.textMin.dispatchEvent(new Event('input', { bubbles: true }));
        const enclosingForm = this.closest && this.closest('form');
        if (enclosingForm) enclosingForm.dispatchEvent(new Event('input', { bubbles: true }));
      } catch (e) {}
    } else {
      const newVal = Math.max(valueAtPointer, Number(this.rangeMin.value || min));
      this.rangeMax.value = String(newVal);
      if (this.textMax) this.textMax.value = newVal === 0 ? '' : String(newVal);
      try {
        if (this.textMax) this.textMax.dispatchEvent(new Event('input', { bubbles: true }));
        const enclosingForm = this.closest && this.closest('form');
        if (enclosingForm) enclosingForm.dispatchEvent(new Event('input', { bubbles: true }));
      } catch (e) {}
    }
    this.updateTrack();
    console.debug && console.debug('PriceRange:trackPointerMove', { active: this.activeThumb, valueAtPointer });
  }

  onDocumentPointerUp(e) {
    // stop move listening
    document.removeEventListener('pointermove', this.onDocumentPointerMove);
    this.activeThumb = null;
    // trigger a final input/change event so any listeners update
    try {
      this.rangeMin && this.rangeMin.dispatchEvent(new Event('change', { bubbles: true }));
      this.rangeMax && this.rangeMax.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (e) {}
    try {
      const enclosingForm = this.closest && this.closest('form');
      if (enclosingForm) enclosingForm.dispatchEvent(new Event('input', { bubbles: true }));
    } catch (e) {}
    console.debug && console.debug('PriceRange:trackPointerUp');
  }

  updateTrack() {
    if (!this.rangeMin || !this.rangeMax || !this.trackFilled) return;
    const min = Number(this.rangeMin.getAttribute('min') || 0);
    const max = Number(this.rangeMax.getAttribute('max') || 0);
    const a = Number(this.rangeMin.value || 0);
    const b = Number(this.rangeMax.value || 0);
    const percentA = ((a - min) / (max - min)) * 100;
    const percentB = ((b - min) / (max - min)) * 100;
    const left = Math.min(percentA, percentB);
    const width = Math.abs(percentB - percentA);
    // debug: expose computed positions
    try {
      console.debug && console.debug('PriceRange:updateTrack', { a, b, min, max, left, width });
    } catch (e) {}
  this.trackFilled.style.display = 'block';
  this.trackFilled.style.left = `${left}%`;
  this.trackFilled.style.width = `${width}%`;
  // Defensive inline styles to ensure visibility (overrides external CSS)
  this.trackFilled.style.background = '#000';
  this.trackFilled.style.top = '0';
  this.trackFilled.style.height = this.trackFilled.style.height || '100%';
  this.trackFilled.style.zIndex = '2';
  this.trackFilled.style.borderRadius = this.trackFilled.style.borderRadius || '6px';
  this.trackFilled.style.pointerEvents = 'none';
  this.trackFilled.style.opacity = '1';
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
